import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { 
  Building2, 
  Users, 
  FileCheck, 
  Shield, 
  Clock, 
  CheckCircle2, 
  ArrowRight,
  Briefcase,
  Globe,
  Zap,
  BarChart3,
  Mail,
  Phone,
  BadgeCheck,
  Layers,
  Code,
  Palette,
  HeadphonesIcon,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import B2BCalculator from '../components/B2BCalculator';
import LanguageSelector from '../components/LanguageSelector';

const B2BPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    companyType: '',
    monthlyVolume: '',
    interest: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Pro subscription state
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeCompany, setSubscribeCompany] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;
  
  // Map plan names to API keys
  const planApiKeys = {
    'Solo': 'solo',
    'Pro Desk': 'pro_desk',
    'Agency': 'agency',
    'White Label': 'white_label',
    'Enterprise': 'enterprise'
  };
  
  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeEmail || !selectedPlan) {
      toast.error('Email is required');
      return;
    }
    
    setSubscribing(true);
    try {
      const response = await fetch(`${API}/api/pro/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: planApiKeys[selectedPlan.name],
          email: subscribeEmail,
          company_name: subscribeCompany
        })
      });
      
      const data = await response.json();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('An error occurred. Please try again.');
    }
    setSubscribing(false);
  };
  
  const openSubscribeModal = (plan) => {
    setSelectedPlan(plan);
    setShowSubscribeModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company || !formData.email || !formData.name) {
      toast.error(t('errors.required_photos'));
      return;
    }
    
    setSubmitting(true);
    try {
      await fetch(`${API}/api/b2b-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      toast.success(t('b2b.form_success'));
      setFormData({
        company: '', name: '', email: '', phone: '',
        companyType: '', monthlyVolume: '', interest: '', message: ''
      });
    } catch (error) {
      toast.success(t('b2b.form_success'));
    }
    setSubmitting(false);
  };

  const getPlans = () => [
    {
      name: 'Solo',
      nameKey: 'plan_solo',
      subtitle: t('b2b.plan_solo_subtitle'),
      price: '$149',
      period: t('b2b.per_month'),
      description: t('b2b.plan_solo_desc'),
      features: [
        t('b2b.feature_user_seat'),
        t('b2b.feature_checks_10'),
        t('b2b.feature_personal_dashboard'),
        t('b2b.feature_basic_pdf'),
        t('b2b.feature_email_support'),
        t('b2b.feature_case_history')
      ],
      extra: t('b2b.extra_15'),
      cta: t('b2b.plan_solo_cta'),
      popular: false,
      color: 'slate'
    },
    {
      name: 'Pro Desk',
      nameKey: 'plan_pro_desk',
      subtitle: t('b2b.plan_pro_desk_subtitle'),
      price: '$399',
      period: t('b2b.per_month'),
      description: t('b2b.plan_pro_desk_desc'),
      features: [
        t('b2b.feature_team_seats_3'),
        t('b2b.feature_checks_30'),
        t('b2b.feature_team_dashboard'),
        t('b2b.feature_case_tracking'),
        t('b2b.feature_light_branding'),
        t('b2b.feature_priority_support')
      ],
      extra: t('b2b.extra_12'),
      cta: t('b2b.plan_pro_desk_cta'),
      popular: false,
      color: 'blue'
    },
    {
      name: 'Agency',
      nameKey: 'plan_agency',
      subtitle: t('b2b.plan_agency_subtitle'),
      price: '$799',
      period: t('b2b.per_month'),
      description: t('b2b.plan_agency_desc'),
      features: [
        t('b2b.feature_team_seats_10'),
        t('b2b.feature_checks_100'),
        t('b2b.feature_logo_pdf'),
        t('b2b.feature_internal_notes'),
        t('b2b.feature_client_reminders'),
        t('b2b.feature_dedicated_channel')
      ],
      extra: t('b2b.extra_8'),
      cta: t('b2b.plan_agency_cta'),
      popular: true,
      color: 'blue'
    },
    {
      name: 'White Label',
      nameKey: 'plan_white_label',
      subtitle: t('b2b.plan_white_label_subtitle'),
      price: '$1,990',
      period: t('b2b.per_month'),
      setup: t('b2b.setup_5000'),
      description: t('b2b.plan_white_label_desc'),
      features: [
        t('b2b.feature_subdomain'),
        t('b2b.feature_full_brand'),
        t('b2b.feature_branded_pdfs'),
        t('b2b.feature_client_portal'),
        t('b2b.feature_checks_200'),
        t('b2b.feature_dedicated_manager')
      ],
      extra: t('b2b.extra_7'),
      cta: t('b2b.plan_white_label_cta'),
      popular: false,
      color: 'purple'
    },
    {
      name: 'Enterprise',
      nameKey: 'plan_enterprise',
      subtitle: t('b2b.plan_enterprise_subtitle'),
      price: '$3,990+',
      period: t('b2b.per_month'),
      setup: t('b2b.setup_15000'),
      description: t('b2b.plan_enterprise_desc'),
      features: [
        t('b2b.feature_unlimited_users'),
        t('b2b.feature_custom_domain'),
        t('b2b.feature_api_webhooks'),
        t('b2b.feature_sso'),
        t('b2b.feature_checks_unlimited'),
        t('b2b.feature_sla_support')
      ],
      extra: t('b2b.extra_volume'),
      cta: t('b2b.plan_enterprise_cta'),
      popular: false,
      color: 'slate'
    }
  ];
  
  const plans = getPlans();

  const getTargetAudiences = () => [
    {
      icon: Globe,
      title: t('b2b.audience_relocation'),
      description: t('b2b.audience_relocation_desc'),
      pain: t('b2b.audience_relocation_pain')
    },
    {
      icon: BadgeCheck,
      title: t('b2b.audience_vets'),
      description: t('b2b.audience_vets_desc'),
      pain: t('b2b.audience_vets_pain')
    },
    {
      icon: Briefcase,
      title: t('b2b.audience_concierge'),
      description: t('b2b.audience_concierge_desc'),
      pain: t('b2b.audience_concierge_pain')
    },
    {
      icon: Code,
      title: t('b2b.audience_platforms'),
      description: t('b2b.audience_platforms_desc'),
      pain: t('b2b.audience_platforms_pain')
    }
  ];
  
  const targetAudiences = getTargetAudiences();

  const getBenefits = () => [
    { icon: Clock, title: t('b2b.benefit_save_time'), description: t('b2b.benefit_save_time_desc') },
    { icon: AlertTriangle, title: t('b2b.benefit_reduce_rejections'), description: t('b2b.benefit_reduce_rejections_desc') },
    { icon: TrendingUp, title: t('b2b.benefit_scale'), description: t('b2b.benefit_scale_desc') },
    { icon: Shield, title: t('b2b.benefit_reputation'), description: t('b2b.benefit_reputation_desc') }
  ];
  
  const benefits = getBenefits();

  return (
    <>
      <Helmet>
        <title>PetFlyCheck for Business | B2B Pet Travel Compliance Solutions</title>
        <meta name="description" content="Partner with PetFlyCheck. B2B solutions for pet relocation agencies, veterinarians, and travel professionals. API, white-label, and enterprise options." />
        <meta name="keywords" content="pet relocation agency software, B2B pet travel, white label pet compliance, veterinary export tools, IATA compliance API" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="PetFlyCheck" className="w-10 h-10 md:w-12 md:h-12 object-contain" />
              <div>
                <span className="font-bold text-xl text-slate-900">PetFlyCheck</span>
                <span className="hidden md:inline text-sm text-blue-600 ml-2 font-medium">{t('b2b.for_business')}</span>
              </div>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-slate-600 hover:text-slate-900 text-sm hidden md:block">{t('b2b.consumer_site')}</Link>
              <a href="#contact">
                <Button className="bg-blue-600 hover:bg-blue-700">{t('b2b.contact_sales')}</Button>
              </a>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 md:py-24 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Building2 className="w-4 h-4" />
              {t('b2b.hero_badge')}
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              {t('b2b.hero_main_title')}<br />
              <span className="text-blue-600">{t('b2b.hero_main_title_accent')}</span>
            </h1>
            
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
              {t('b2b.hero_description')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <a href="#pricing">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-6">
                  {t('b2b.view_plans')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href="#contact">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                  {t('b2b.schedule_demo')}
                </Button>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span>{t('b2b.badge_iata')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span>{t('b2b.badge_airlines')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span>{t('b2b.badge_countries')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span>{t('b2b.badge_ai')}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Statement */}
        <section className="py-16 bg-slate-900 text-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="max-w-3xl">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  {t('b2b.problem_title')}
                </h2>
                <div className="space-y-4 text-slate-300">
                  <p>{t('b2b.problem_p1')}</p>
                  <p>
                    <strong className="text-white">{t('b2b.problem_p2')}</strong>
                  </p>
                  <p>{t('b2b.problem_p3')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Benefits */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('b2b.solution_title')}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {t('b2b.solution_subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, idx) => (
                <Card key={idx} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <benefit.icon className="w-7 h-7 text-blue-600" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{benefit.title}</h3>
                    <p className="text-sm text-slate-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Target Audiences */}
        <section className="py-16 bg-slate-50 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('b2b.audience_title')}
              </h2>
              <p className="text-lg text-slate-600">
                {t('b2b.audience_subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {targetAudiences.map((audience, idx) => (
                <Card key={idx} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <audience.icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-2">{audience.title}</h3>
                        <p className="text-slate-600 text-sm mb-3">{audience.description}</p>
                        <p className="text-slate-500 text-xs italic">"{audience.pain}"</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ROI Calculator */}
        <B2BCalculator />

        {/* Pricing Section */}
        <section id="pricing" className="py-16 md:py-24 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('b2b.plans_title')}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {t('b2b.plans_subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {plans.map((plan, idx) => (
                <Card 
                  key={idx} 
                  className={`relative border-2 ${plan.popular ? 'border-blue-500 shadow-xl' : 'border-slate-200'}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      {t('b2b.most_popular')}
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                      <p className="text-sm text-slate-500">{plan.subtitle}</p>
                      <div className="mt-4">
                        <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                        <span className="text-slate-500">{plan.period}</span>
                      </div>
                      {plan.setup && (
                        <p className="text-sm text-blue-600 mt-1">{plan.setup}</p>
                      )}
                    </div>

                    <p className="text-sm text-slate-600 text-center mb-6 min-h-[48px]">
                      {plan.description}
                    </p>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <p className="text-xs text-slate-500 text-center mb-4">
                      {plan.extra}
                    </p>

                    <Button 
                      className={`w-full ${plan.popular ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                      variant={plan.popular ? 'default' : 'outline'}
                      onClick={() => openSubscribeModal(plan)}
                      data-testid={`subscribe-${plan.name.toLowerCase().replace(' ', '-')}-btn`}
                    >
                      {plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Value Reinforcement Note */}
            <div className="mt-12 bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-6 max-w-4xl mx-auto border border-blue-100">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-blue-700 mb-2">{t('b2b.value_note_title')}</p>
                  <p>
                    {t('b2b.value_note_text')} <span className="text-slate-500">{t('b2b.value_note_disclaimer')}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works for B2B */}
        <section className="py-16 bg-blue-600 text-white px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t('b2b.how_it_works_title')}
              </h2>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <h3 className="font-bold mb-2">{t('b2b.step1_title')}</h3>
                <p className="text-blue-100 text-sm">
                  {t('b2b.step1_desc')}
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <h3 className="font-bold mb-2">{t('b2b.step2_title')}</h3>
                <p className="text-blue-100 text-sm">
                  {t('b2b.step2_desc')}
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <h3 className="font-bold mb-2">{t('b2b.step3_title')}</h3>
                <p className="text-blue-100 text-sm">
                  {t('b2b.step3_desc')}
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">4</span>
                </div>
                <h3 className="font-bold mb-2">{t('b2b.step4_title')}</h3>
                <p className="text-blue-100 text-sm">
                  {t('b2b.step4_desc')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-16 md:py-24 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('b2b.contact_title')}
              </h2>
              <p className="text-lg text-slate-600">
                {t('b2b.contact_subtitle')}
              </p>
            </div>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="company" className="text-slate-700">{t('b2b.form_company')} *</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        placeholder={t('b2b.form_company')}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="name" className="text-slate-700">{t('b2b.form_name')} *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder={t('b2b.form_name')}
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-slate-700">{t('b2b.form_email')} *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="you@company.com"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-slate-700">{t('b2b.form_phone')}</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="+1 (555) 000-0000"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="companyType" className="text-slate-700">{t('b2b.form_type')}</Label>
                      <select
                        id="companyType"
                        value={formData.companyType}
                        onChange={(e) => setFormData({...formData, companyType: e.target.value})}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-slate-900"
                      >
                        <option value="">{t('b2b.form_select_type')}</option>
                        <option value="pet-relocation">{t('b2b.form_type_relocation')}</option>
                        <option value="veterinary">{t('b2b.form_type_vet')}</option>
                        <option value="concierge">{t('b2b.form_type_concierge')}</option>
                        <option value="travel-agency">{t('b2b.form_type_travel')}</option>
                        <option value="platform">{t('b2b.form_type_platform')}</option>
                        <option value="franchise">{t('b2b.form_type_franchise')}</option>
                        <option value="other">{t('b2b.form_type_other')}</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="monthlyVolume" className="text-slate-700">{t('b2b.form_volume')}</Label>
                      <select
                        id="monthlyVolume"
                        value={formData.monthlyVolume}
                        onChange={(e) => setFormData({...formData, monthlyVolume: e.target.value})}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-slate-900"
                      >
                        <option value="">{t('b2b.form_select_volume')}</option>
                        <option value="1-10">{t('b2b.form_volume_1_10')}</option>
                        <option value="11-30">{t('b2b.form_volume_11_30')}</option>
                        <option value="31-75">{t('b2b.form_volume_31_75')}</option>
                        <option value="76-150">{t('b2b.form_volume_76_150')}</option>
                        <option value="150+">{t('b2b.form_volume_150_plus')}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="interest" className="text-slate-700">{t('b2b.form_interest')}</Label>
                    <select
                      id="interest"
                      value={formData.interest}
                      onChange={(e) => setFormData({...formData, interest: e.target.value})}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-slate-900"
                    >
                      <option value="">{t('b2b.form_select_plan')}</option>
                      <option value="solo">{t('b2b.form_interest_solo')}</option>
                      <option value="pro-desk">{t('b2b.form_interest_pro_desk')}</option>
                      <option value="agency">{t('b2b.form_interest_agency')}</option>
                      <option value="white-label">{t('b2b.form_interest_white_label')}</option>
                      <option value="enterprise">{t('b2b.form_interest_enterprise')}</option>
                      <option value="demo">{t('b2b.form_interest_demo')}</option>
                      <option value="not-sure">{t('b2b.form_interest_help')}</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-slate-700">{t('b2b.form_message')}</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder={t('b2b.form_placeholder_message')}
                      className="mt-1 min-h-[100px]"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg"
                    disabled={submitting}
                  >
                    {submitting ? t('common.loading') : t('b2b.form_submit')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-8 text-center text-slate-500 text-sm">
              <p>{t('b2b.email_direct')} <a href="mailto:contact@petflycheck.io" className="text-blue-600 hover:underline">contact@petflycheck.io</a></p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-slate-400 py-12">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="PetFlyCheck" className="w-10 h-10 object-contain" />
                <div>
                  <span className="text-white font-semibold">PetFlyCheck</span>
                  <span className="text-blue-400 ml-2 text-sm">{t('b2b.for_business')}</span>
                </div>
              </div>
              <div className="flex items-center gap-8 text-sm">
                <Link to="/" className="hover:text-white transition-colors">{t('b2b.consumer_site')}</Link>
                <Link to="/airlines" className="hover:text-white transition-colors">{t('nav.airlines', 'Airlines')}</Link>
                <Link to="/destinations" className="hover:text-white transition-colors">{t('nav.destinations', 'Destinations')}</Link>
                <a href="#pricing" className="hover:text-white transition-colors">{t('nav.pricing', 'Pricing')}</a>
                <a href="#contact" className="hover:text-white transition-colors">{t('common.contact')}</a>
              </div>
            </div>
            <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm">
              <p>{t('b2b.footer_rights')}</p>
            </div>
          </div>
        </footer>
      </div>
      
      {/* Subscribe Modal */}
      {showSubscribeModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowSubscribeModal(false)}>
          <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-slate-900">{t('b2b.subscribe_title', { plan: selectedPlan.name })}</h3>
                <p className="text-slate-600 mt-2">{selectedPlan.price}{selectedPlan.period}</p>
                {selectedPlan.setup && <p className="text-sm text-blue-600">{selectedPlan.setup}</p>}
              </div>
              
              <form onSubmit={handleSubscribe} className="space-y-4">
                <div>
                  <Label htmlFor="subscribe-email" className="text-slate-700">{t('b2b.form_email')} *</Label>
                  <Input
                    id="subscribe-email"
                    type="email"
                    value={subscribeEmail}
                    onChange={(e) => setSubscribeEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="mt-1"
                    required
                    data-testid="subscribe-email-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="subscribe-company" className="text-slate-700">{t('b2b.form_company')}</Label>
                  <Input
                    id="subscribe-company"
                    value={subscribeCompany}
                    onChange={(e) => setSubscribeCompany(e.target.value)}
                    placeholder={t('b2b.form_company')}
                    className="mt-1"
                    data-testid="subscribe-company-input"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowSubscribeModal(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={subscribing}
                    data-testid="subscribe-confirm-btn"
                  >
                    {subscribing ? t('common.loading') : t('b2b.subscribe_confirm')}
                  </Button>
                </div>
              </form>
              
              <p className="text-xs text-slate-500 text-center mt-4">
                {t('b2b.subscribe_secure')}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default B2BPage;
